import './polyfills/intersectionObserver'
import './polyfills/resizeObserver'
import './polyfills/preventDefault'
import './polyfills/urlSearchParams'
import './polyfills/url'
import './polyfills/objectFit'
import './polyfills/smoothScroll'
import React from 'react'
import ReactDOM from 'react-dom'
import App from '#src/containers/App'
import { ApolloProvider } from '@apollo/client'
import { Router } from 'react-router-dom'
import { createBrowserHistory, createPath } from 'history'
import { HelmetProvider } from 'react-helmet-async'
import { Identity } from '@deal/web-tracking/constants'
import StalePageRefresher from '@deal/refresh-stale-page'
import PageInteractionObserver from '@deal/page-interaction-observer'
import { AnalyticsProvider } from '#src/containers/Analytics'
import { IdentityProvider } from '#src/containers/Identity'
import { ConfigProvider } from '#src/containers/Config'
import { HostnameProvider, Hostname } from '#src/containers/Hostname'
import { UserAgentProvider } from '#src/containers/UserAgent'
import { CookieProvider, DEFAULT_COOKIE_OPTIONS } from '#src/context/cookies'
import { GlobalNotificationProvider } from '#src/context/globalNotification'
import { PageKeyProvider } from '@deal/page-key'
import config from '#src/config'
import Loadable from 'react-loadable'
import schemaFragments from '#src/generated/schema.fragments'
import schemaNonNormalizableTypes from '#src/generated/schema.non-normalizable-types'
import schemaRelayStylePaginationFields from '#src/generated/schema.relay-style-pagination-fields'
import {
  browserDealApiClient,
  BrowserDealApiClientOptions,
  DealViewerContext
} from '@deal/api-client-js'
import UserExperimentClientProvider from '#src/containers/UserExperimentClientProvider'
import {
  createBrowserExperimentClient,
  BrowserExperimentClientConfig,
  ExperimentViewedInput
} from '@deal/experiment-js'
import { ErrorResponse } from '@apollo/client/link/error'
import Heartbeat from '#src/components/Heartbeat'
import SendTokensToNative from '#src/services/sendTokensToNative'
import { getForceTraceId } from '#src/services/tracing'
import { loginLocation } from '#src/services/routing'
import { init as SentryInit } from '@sentry/browser'
import { captureNetworkError, captureGraphQLError } from '#src/services/logger'
import {
  WebMessageEventEmitter,
  WebMessageEventEmitterProvider
} from '#src/containers/ReceiveMessageFromNative'
import trimStart from 'lodash/trimStart'
import cookies from 'js-cookie'
import uuidv4 from 'uuid/v4'
import { MyselfDocument, MyselfQuery } from '#src/containers/Identity/Myself.generated'

import {
  TrackOnlineActivityDocument,
  TrackOnlineActivityMutation,
  TrackOnlineActivityMutationVariables
} from '#src/mutations/TrackOnlineActivity.generated'

// Exception handling
if (config.get('sentry.enabled')) {
  SentryInit({
    dsn: config.get('sentry.webDsn'),
    environment: config.get('environment'),
    release: process.env.SENTRY_RELEASE
  })
}

// Refresh the page/tab if it's been backgrounded for over 1 hour
StalePageRefresher.monitor()

const isBot = window.__IS_BOT__
const isSyntheticTest = window.navigator.userAgent.includes('PTST/SpeedCurve')

// Configure the hostname
const fullHostname = window.location.hostname.toLowerCase()
let hostname = Hostname.Deal
let isGcp = false
if (fullHostname.endsWith(config.get('gcpHostSuffix'))) {
  hostname = Hostname.Curated
  isGcp = true
} else if (fullHostname.endsWith(config.get('curatedHostSuffix'))) {
  hostname = Hostname.Curated
}

// GraphQL API connection details
let api = config.get('api')

if (isGcp) {
  api = config.get('apiGcp')
} else if (hostname === Hostname.Curated) {
  api = config.get('apiCurated')
}

// Allow override of optimizely variations via the url
const urlQueryString = window.location.search
const urlParams = new URLSearchParams(urlQueryString)
const experimentOverrides = urlParams.get('experimentOverrides') || undefined
const forceDefaultTreatments =
  urlParams.get('forceDefaultTreatments') === 'true' || isBot || isSyntheticTest
const experimentBucketingKey = urlParams.get('experimentBucketingKey') || undefined

// Callback for Apollo GraphQL errors
const handleError = (error: ErrorResponse) => {
  const { graphQLErrors, networkError, operation } = error

  if (graphQLErrors) {
    graphQLErrors.forEach(error => {
      captureGraphQLError(error, operation)
    })
  }

  if (networkError) {
    captureNetworkError(networkError, operation)
  }
}

// History to be used by react router
const history = createBrowserHistory()

const handleNetworkError = (networkError: Error) => {
  // @ts-ignore `networkError` is incompletely typed
  //   See: https://github.com/apollographql/apollo-link/issues/300
  if (networkError && networkError.statusCode == 401) {
    // Redirect to login on 401
    window.location.href = createPath(loginLocation(history.location))
  }
}

const sendTokensToNative = new SendTokensToNative()
// Bootstrap the native client with the initial state of the tokens
window.addEventListener('load', sendTokensToNative.sendIfChanged)

// Intercept DealNativeBridge.sendToWeb calls, dispatching them through this event listener
const webMessageEventEmitter = new WebMessageEventEmitter()
webMessageEventEmitter.interceptBridgeSendToWeb()

// GraphQL client
const { apolloClient } = browserDealApiClient({
  protocol: api.protocol,
  webSocketProtocol: api.webSocketProtocol,
  host: api.host,
  port: api.port,
  cachePossibleTypes: schemaFragments.possibleTypes,
  cacheNonNormalizableTypes: schemaNonNormalizableTypes,
  cacheRelayStylePaginationFields: schemaRelayStylePaginationFields,
  initialCacheState: window.__APOLLO_STATE__,
  experimentOverrides,
  forceTrace: () => getForceTraceId(window.location.href),
  isBot: () => isBot,
  viewerContext: () => DealViewerContext.CONSUMER,
  identityFromJwtPayload: jwtPayload => {
    return jwtPayload.sub
  },
  identityFromCache: cache => {
    return cache.readQuery<MyselfQuery>({
      query: MyselfDocument
    })?.me?.user?.id
  },
  onIdentityMismatch: () => {
    // If the user ID in the Apollo cache doesn't match the JWT, refresh the
    //   page to synchronize the two, but wait a second to allow the error to
    //   propagate to Sentry
    setTimeout(() => {
      window.location.reload()
    }, 500)
  },
  onError: handleError,
  onNetworkError: handleNetworkError,
  recordOperation: (operationKind, operationName, _operationVariables, result) => {
    //@ts-ignore
    const recordingFn = window.__recordGraphQLOperationToAsayer as BrowserDealApiClientOptions['recordOperation']

    if (recordingFn) {
      // Remove GQL variables since they can contain sensitive information
      return recordingFn(operationKind, operationName, {}, result)
    } else {
      return result
    }
  },
  links: [sendTokensToNative.sendIfChangedOnResponseLink()],
  clientAwarenessName: 'consumer-app-client-' + config.get('environment'),
  clientAwarenessVersion: window.__APP_VERSION__
})
delete window.__APOLLO_STATE__

// Rendering
apolloClient
  .query<MyselfQuery>({
    query: MyselfDocument
  })
  .then(({ data }) => {
    const userId = data && data.me && data.me.id
    const realUserId = data && data.me && data.me.realUser.id

    const tracking = window.tracking

    if (userId !== realUserId) {
      tracking?.disable()
    }

    if (userId) {
      tracking?.identify(userId, Identity.Consumer)
    }

    // Inform the backend that this user is active on the site
    const trackOnlineActivity = () => {
      // Only track non-impersonated users
      if (!userId || userId !== realUserId) {
        return
      }

      apolloClient
        .mutate<TrackOnlineActivityMutation, TrackOnlineActivityMutationVariables>({
          mutation: TrackOnlineActivityDocument,
          variables: {
            input: {
              id: userId
            }
          }
        })
        // Errors will be handled by our global error handler
        .catch(() => {})
    }

    // Track initial user activity (loading this page)
    trackOnlineActivity()

    // Track ongoing user activity (moving mouse, scrolling, etc.)
    const interactionObserver = new PageInteractionObserver(() => {
      trackOnlineActivity()
    })
    interactionObserver.observe()

    // Update RUM page label and @deal/web-tracking if page-key changes
    const onPageKeyChange = (pageKey: string | null) => {
      tracking?.setPageKey(pageKey || undefined)

      if (pageKey) {
        if (window.LUX) {
          window.LUX.label = pageKey
        }
      } else {
        if (window.LUX) {
          delete window.LUX.label
        }
      }
    }

    const queryParams = new URLSearchParams(trimStart(window.location.search, '?'))
    const previousClientSessionId = cookies.get('curated_client_session_id')
    const shouldResetClientSession =
      queryParams.has('utm_source') ||
      queryParams.has('gclid') ||
      queryParams.has('fbclid') ||
      !previousClientSessionId

    if (shouldResetClientSession) {
      const newClientSessionId = uuidv4()

      tracking?.events.trackClientSessionReset({
        current_id: newClientSessionId,
        previous_id: previousClientSessionId
      })

      cookies.set('curated_client_session_id', newClientSessionId, DEFAULT_COOKIE_OPTIONS)
    }

    // Experiment client
    const fallbackTreatments = window.__EXPERIMENT_TREATMENTS__

    const handleExperimentViewed = ({
      experiment,
      variation,
      changeNumber,
      nonInteraction
    }: ExperimentViewedInput) => {
      tracking?.events.trackExperimentViewed({
        experiment,
        variation,
        change_number: changeNumber,
        non_interaction: nonInteraction
      })

      /**
       * Add experiment & variation data to SpeedCurve beacon so we can segment LUX user's by treatment
       */
      if (window.LUX) {
        window.LUX.addData(experiment, variation)
      }
    }

    const experimentClientConfig: BrowserExperimentClientConfig = {
      graphqlConfig: {
        apolloClient,
        fetchUpdatesIntervalMillis: 60000
      },
      onExperimentViewed: handleExperimentViewed,
      experimentOverrides,
      forceDefaultTreatments,
      fallback: fallbackTreatments
    }
    const experimentClient = createBrowserExperimentClient(experimentClientConfig)
    delete window.__OPTIMIZELY_DATAFILE__
    delete window.__EXPERIMENT_TREATMENTS__

    // Rendering method
    const renderer = window.__APP_RENDERER__ || 'client'
    delete window.__APP_RENDERER__

    // Rendering function
    const render = (AppComponent: React.ComponentType) => {
      const Root = (
        <ConfigProvider config={config}>
          <ApolloProvider client={apolloClient}>
            <CookieProvider environment={window}>
              <UserAgentProvider userAgent={window.navigator.userAgent} isBot={isBot}>
                <AnalyticsProvider client={tracking}>
                  <IdentityProvider>
                    <Heartbeat />
                    <UserExperimentClientProvider
                      experimentClient={experimentClient}
                      overrideId={experimentBucketingKey}
                    >
                      <HostnameProvider hostname={hostname}>
                        <WebMessageEventEmitterProvider value={webMessageEventEmitter}>
                          <Router history={history}>
                            <PageKeyProvider onNewPageKey={onPageKeyChange}>
                              <HelmetProvider>
                                <GlobalNotificationProvider>
                                  <AppComponent />
                                </GlobalNotificationProvider>
                              </HelmetProvider>
                            </PageKeyProvider>
                          </Router>
                        </WebMessageEventEmitterProvider>
                      </HostnameProvider>
                    </UserExperimentClientProvider>
                  </IdentityProvider>
                </AnalyticsProvider>
              </UserAgentProvider>
            </CookieProvider>
          </ApolloProvider>
        </ConfigProvider>
      )

      if (renderer === 'server') {
        Loadable.preloadReady().then(() => {
          ReactDOM.hydrate(Root, document.getElementById('deal'))
        })
      } else {
        ReactDOM.render(Root, document.getElementById('deal'))
      }
    }

    render(App)
  })
